import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import { SOVEREIGN } from '../../epc/utils/epcProjectVisual';
import { useFeatureForm, FeatureFormMode } from './useFeatureForm';
import { FeatureWizardHeader } from './FeatureWizardHeader';
import { BasicsStep } from './steps/BasicsStep';
import { PlacementStep } from './steps/PlacementStep';
import { PermissionsStep } from './steps/PermissionsStep';
import { ReviewStep } from './steps/ReviewStep';
import { mapFeatureApiError } from './utils/mapFeatureApiError';
import { featureService } from '../../../services/featureService';
import type { Feature, FeatureDepartment } from '../../../types/feature';

const LAST_STEP = 3;

const FIELD_STEP: Record<string, number> = {
  name: 0, key: 0, icon: 0, route: 1, businessUnit: 1, module: 2, perm: 2,
};

interface Props {
  open: boolean;
  mode: FeatureFormMode;
  onClose: () => void;
  onSuccess: () => void;
  defaultDepartment?: FeatureDepartment;
  feature?: Feature | null;
  existingKeys?: Set<string>;
}

export function FeatureFormDrawer({ open, mode, onClose, onSuccess, defaultDepartment, feature, existingKeys }: Props) {
  const form = useFeatureForm({ mode, isOpen: open, defaultDepartment, feature });
  const [step, setStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setMaxStepReached(mode === 'edit' ? LAST_STEP : 0);
    }
  }, [open, mode]);

  const isEdit = mode === 'edit';
  const stepHasError = [
    !!(form.errors.name || form.errors.key || form.errors.icon),
    !!(form.errors.route || form.errors.businessUnit),
    !!(form.errors.module || form.errors.perm),
    false,
  ];

  const goNext = () => {
    if (step === 0 && !isEdit && existingKeys && form.key.trim() && existingKeys.has(form.key.trim())) {
      form.setErrors(prev => ({ ...prev, key: 'A feature with this key already exists' }));
      return;
    }
    if (!form.validateStep(step)) return;
    const next = Math.min(step + 1, LAST_STEP);
    setStep(next);
    setMaxStepReached(m => Math.max(m, next));
  };

  const goBack = () => setStep(s => Math.max(0, s - 1));

  const jump = (target: number) => {
    if (target <= step) { setStep(target); return; }
    for (let i = step; i < target; i++) if (!form.validateStep(i)) { setStep(i); return; }
    setStep(target);
    setMaxStepReached(m => Math.max(m, target));
  };

  const handleSubmit = async () => {
    for (let i = 0; i <= 2; i++) if (!form.validateStep(i)) { setStep(i); return; }
    setSubmitting(true);
    try {
      const payload = form.buildPayload();
      if (isEdit && feature) await featureService.updateFeatureByKey(feature.key, payload);
      else await featureService.createFeature(payload);
      toast.success(isEdit ? 'Feature updated successfully' : 'Feature created successfully');
      onSuccess();
      onClose();
    } catch (err) {
      const { fieldErrors, message } = mapFeatureApiError(err);
      form.setErrors(prev => ({ ...prev, ...fieldErrors }));
      const firstField = Object.keys(fieldErrors)[0];
      if (firstField && FIELD_STEP[firstField] !== undefined) setStep(FIELD_STEP[firstField]);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div
      className="flex items-center justify-between px-6 py-4"
      style={{
        borderTop: `1px solid ${SOVEREIGN.innerRule}`,
        background: SOVEREIGN.cardBg,
      }}
    >
      <button
        type="button"
        onClick={onClose}
        className="px-5 py-2 text-sm font-medium rounded-xl transition-colors"
        style={{
          border: `1px solid ${SOVEREIGN.cardBorder}`,
          background: SOVEREIGN.inputBg,
          color: SOVEREIGN.ink,
        }}
      >
        Cancel
      </button>
      <div className="flex gap-3">
        {step > 0 && (
          <button
            type="button"
            onClick={goBack}
            className="px-5 py-2 text-sm font-medium rounded-xl transition-colors"
            style={{
              border: `1px solid ${SOVEREIGN.cardBorder}`,
              background: SOVEREIGN.inputBg,
              color: SOVEREIGN.ink,
            }}
          >
            ← Back
          </button>
        )}
        {step < LAST_STEP ? (
          <button
            type="button"
            onClick={goNext}
            className="px-6 py-2 text-sm font-bold text-white rounded-xl transition-colors"
            style={{ background: SOVEREIGN.btn, boxShadow: SOVEREIGN.btnShadow }}
          >
            Next →
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 text-sm font-bold text-white rounded-xl disabled:opacity-50 transition-colors"
            style={{ background: SOVEREIGN.btn, boxShadow: SOVEREIGN.btnShadow }}
          >
            {submitting ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create Feature'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <RightDrawer open={open} onClose={onClose} size="2xl" footer={footer}>
      <FeatureWizardHeader
        title={isEdit ? 'Edit Feature' : 'Create New Feature'}
        subtitle={isEdit && feature ? `Key: ${feature.key}` : 'Define a new menu item with permissions and business-unit scope'}
        current={step}
        maxStepReached={maxStepReached}
        stepHasError={stepHasError}
        onClose={onClose}
        onJump={jump}
      />
      <form onSubmit={e => { e.preventDefault(); if (step < LAST_STEP) goNext(); else handleSubmit(); }}>
        {step === 0 && <BasicsStep form={form} />}
        {step === 1 && <PlacementStep form={form} />}
        {step === 2 && <PermissionsStep form={form} />}
        {step === 3 && <ReviewStep form={form} />}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </RightDrawer>
  );
}

export default FeatureFormDrawer;
